import React from 'react';
import { Check, ArrowRight, BookOpen, Users, Monitor, Layers } from 'lucide-react';
import { PageTransition } from '../components/layout/PageTransition';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
export function Courses() {
  const {
    t
  } = useLanguage();
  const primaryCourses = [{
    id: 'conflict-coaching',
    path: '/services/courses/conflict-coaching',
    icon: <Users size={32} data-id="element-341" />,
    color: 'bg-coral',
    lightColor: 'bg-coral/10',
    textColor: 'text-coral',
    borderColor: 'border-coral/20',
    tKey: 'conflictCoaching'
  }, {
    id: 'first-time-manager',
    path: '/services/courses/first-time-manager',
    icon: <Users size={32} data-id="element-342" />,
    color: 'bg-teal',
    lightColor: 'bg-teal/10',
    textColor: 'text-teal',
    borderColor: 'border-teal/20',
    tKey: 'firstTimeManager'
  }];
  const secondaryCourses = [{
    id: 'art-of-facilitation',
    path: '/services/courses/art-of-facilitation',
    icon: <Layers size={24} data-id="element-343" />,
    lightColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-200',
    titleKey: 'courses.additional.artOfFacilitation.title',
    subtitleKey: 'courses.additional.artOfFacilitation.subtitle',
    descKey: 'courses.additional.artOfFacilitation.desc'
  }, {
    id: 'better-online-meetings',
    path: '/services/courses/better-online-meetings',
    icon: <Monitor size={24} data-id="element-344" />,
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    titleKey: 'courses.items.betterOnlineMeetings.title',
    subtitleKey: 'courses.items.betterOnlineMeetings.subtitle',
    descKey: 'courses.items.betterOnlineMeetings.description'
  }, {
    id: 'better-online-workshops',
    path: '/services/courses/better-online-workshops',
    icon: <Layers size={24} data-id="element-345" />,
    lightColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200',
    titleKey: 'courses.items.betterOnlineWorkshops.title',
    subtitleKey: 'courses.items.betterOnlineWorkshops.subtitle',
    descKey: 'courses.items.betterOnlineWorkshops.description'
  }];
  return <PageTransition data-id="element-346">
      <div className="pt-12 pb-24" data-id="element-347">
        <section className="text-center max-w-4xl mx-auto px-4 mb-20 relative" data-id="element-348">
          <DecorativeShape type="square" color="bg-coral/10" className="top-0 left-0 w-32 h-32 -rotate-12 -z-10" data-id="element-349" />
          <DecorativeShape type="donut" color="border-teal/10" className="bottom-0 right-0 w-40 h-40 -z-10" data-id="element-350" />
          <Animate data-id="element-351">
            <span className="inline-block px-4 py-2 bg-coral/10 text-coral font-medium rounded-full text-sm mb-6" data-id="element-352">{t('courses.badge')}</span>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-353">{t('courses.title')}</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-354">{t('courses.subtitle')}</p>
          </Animate>
        </section>

        {/* Primary courses */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-355">
          <div className="space-y-12" data-id="element-356">
            {primaryCourses.map((course, index) => {
            const benefits = t(`courses.items.${course.tKey}.benefits`) as string[];
            return <Animate key={course.id} delay={0} data-id="element-357">
                  <Card className={`p-0 overflow-hidden border-2 ${course.borderColor}`} data-id="element-358">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-0" data-id="element-359">
                      <div className={`lg:col-span-4 ${course.lightColor} p-10 flex flex-col justify-center items-center text-center relative overflow-hidden`} data-id="element-360">
                        <div className={`w-24 h-24 rounded-full ${course.color} text-white flex items-center justify-center mb-6 shadow-lg`} data-id="element-361">{course.icon}</div>
                        <h3 className={`text-2xl font-bold ${course.textColor} mb-2`} data-id="element-362">{t(`courses.items.${course.tKey}.title`)}</h3>
                        <p className="font-medium text-gray-600 mb-4" data-id="element-363">{t(`courses.items.${course.tKey}.subtitle`)}</p>
                      </div>
                      <div className="lg:col-span-8 p-8 md:p-12 flex flex-col justify-center bg-white" data-id="element-364">
                        <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-365">{t(`courses.items.${course.tKey}.description`)}</p>
                        <div className="flex flex-wrap gap-6 mb-8 text-sm" data-id="element-366">
                          <div className="flex items-center gap-2 text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg" data-id="element-367">
                            <Monitor size={18} className={course.textColor} data-id="element-368" />
                            <span className="font-medium" data-id="element-369">{t(`courses.items.${course.tKey}.format`)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg" data-id="element-370">
                            <Users size={18} className={course.textColor} data-id="element-371" />
                            <span className="font-medium" data-id="element-372">{t(`courses.items.${course.tKey}.audience`)}</span>
                          </div>
                        </div>
                        <div className="mb-8" data-id="element-373">
                          <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4" data-id="element-374">{t('courses.benefitsTitle')}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-375">
                            {benefits.map((benefit: string, i: number) => <div key={i} className="flex items-start" data-id="element-376">
                                <div className={`mt-1 mr-3 p-1 rounded-full ${course.lightColor} ${course.textColor}`} data-id="element-377"><Check size={12} strokeWidth={3} data-id="element-378" /></div>
                                <span className="text-gray-700" data-id="element-379">{benefit}</span>
                              </div>)}
                          </div>
                        </div>
                        <div className="pt-6 border-t border-gray-100" data-id="element-380">
                          <Link to={course.path} data-id="element-381">
                            <Button variant={index === 1 ? 'secondary' : 'primary'} rightIcon={<ArrowRight size={18} data-id="element-383" />} data-id="element-382">
                              {t('courses.moreInfo')}
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Animate>;
          })}
          </div>
        </section>

        {/* Secondary / facilitation skills courses */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-384">
          <Animate className="mb-8" data-id="element-385">
            <h2 className="text-2xl font-bold text-charcoal" data-id="element-386">{t('courses.additional.title')}</h2>
            <p className="text-gray-500 mt-1" data-id="element-387">{t('courses.additional.subtitle')}</p>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-388">
            {secondaryCourses.map(course => <Animate key={course.id} data-id="element-389">
                <Card className={`p-6 border-2 ${course.borderColor} h-full flex flex-col`} hoverEffect data-id="element-390">
                  <div className={`p-3 rounded-2xl ${course.lightColor} w-fit mb-4`} data-id="element-391">
                    <div className={course.textColor} data-id="element-392">{course.icon}</div>
                  </div>
                  <div className="mb-1" data-id="element-393">
                    <span className={`text-xs font-bold uppercase tracking-wide ${course.textColor}`} data-id="element-394">
                      {t(course.subtitleKey)}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-charcoal mb-2" data-id="element-395">{t(course.titleKey)}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-4" data-id="element-396">{t(course.descKey)}</p>
                  <Link to={course.path} data-id="element-397">
                    <Button variant="outline" size="sm" rightIcon={<ArrowRight size={14} data-id="element-399" />} data-id="element-398">{t('courses.moreInfo')}</Button>
                  </Link>
                </Card>
              </Animate>)}
          </div>
        </section>

        {/* Custom CTA */}
        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-400">
          <Animate data-id="element-401">
            <Card className="p-12 bg-gradient-to-br from-teal/5 to-coral/5 border-0" data-id="element-402">
              <div className="w-16 h-16 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-sm" data-id="element-403">
                <BookOpen size={32} className="text-charcoal" data-id="element-404" />
              </div>
              <h2 className="text-3xl font-bold mb-4" data-id="element-405">{t('courses.customTitle')}</h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-406">{t('courses.customDescription')}</p>
              <Link to="/contact" data-id="element-407">
                <Button variant="secondary" rightIcon={<ArrowRight size={18} data-id="element-409" />} data-id="element-408">{t('courses.contactUs')}</Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}